// api/services/productService.js
import api from '../interceptors/axiosInterceptor'
import API_CONFIG from '../config'

const PRODUCT_ENDPOINTS = {
  LIST: '/products',
  DETAIL: '/products/:id',
  SEARCH: '/products/search',
  CATEGORY: '/products/category/:categoryId',
  RELATED: '/products/:id/related',
  NEWEST: '/products/newest',
  POPULAR: '/products/popular',
  DISCOUNTED: '/products/discounted',
  ADD_TO_CART: '/cart/add',
  REMOVE_FROM_CART: '/cart/remove',
  UPDATE_CART: '/cart/update',
  GET_CART: '/cart',
  WISHLIST: '/wishlist',
  ADD_TO_WISHLIST: '/wishlist/add',
  REMOVE_FROM_WISHLIST: '/wishlist/remove'
}

class ProductService {
  // ========== متدهای اصلی محصولات ==========

  // دریافت لیست محصولات
  async getProducts(params = {}) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${PRODUCT_ENDPOINTS.LIST}`,
        { params }
      )

      return {
        success: true,
        data: response.data,
        message: 'محصولات دریافت شد'
      }
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // دریافت جزئیات یک محصول
  async getProductById(productId) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${PRODUCT_ENDPOINTS.DETAIL.replace(':id', productId)}`
      )

      return {
        success: true,
        data: response.data,
        message: 'محصول دریافت شد'
      }
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // جستجوی محصولات
  async searchProducts(query, params = {}) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${PRODUCT_ENDPOINTS.SEARCH}`,
        {
          params: {
            q: query,
            ...params
          }
        }
      )

      return {
        success: true,
        data: response.data,
        message: 'نتایج جستجو دریافت شد'
      }
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // دریافت محصولات بر اساس دسته‌بندی
  async getProductsByCategory(categoryId, params = {}) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${PRODUCT_ENDPOINTS.CATEGORY.replace(':categoryId', categoryId)}`,
        { params }
      )

      return {
        success: true,
        data: response.data,
        message: 'محصولات دسته‌بندی دریافت شد'
      }
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // دریافت محصولات مرتبط
  async getRelatedProducts(productId, limit = 4) {
    try {
      const storeSlug = window.__STORE_INFO__?.slug || 'mmd1';
      const url = `${API_CONFIG.BASE_URL}${SHOP_ENDPOINTS.PRODUCTS_BY_CATEGORY}${storeSlug}/`;
      const response = await api.get(url, {
        params: {
          category: categorySlug,
          ...params
        }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // دریافت جدیدترین محصولات
  async getNewestProducts(limit = 10) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${PRODUCT_ENDPOINTS.NEWEST}`,
        { params: { limit } }
      )

      return {
        success: true,
        data: response.data,
        message: 'جدیدترین محصولات دریافت شد'
      }
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // دریافت پربازدیدترین محصولات
  async getPopularProducts(limit = 10) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${PRODUCT_ENDPOINTS.POPULAR}`,
        { params: { limit } }
      )

      return {
        success: true,
        data: response.data,
        message: 'محصولات پربازدید دریافت شد'
      }
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // دریافت محصولات تخفیف‌دار
  async getDiscountedProducts(limit = 10) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${PRODUCT_ENDPOINTS.DISCOUNTED}`,
        { params: { limit } }
      )

      return {
        success: true,
        data: response.data,
        message: 'محصولات تخفیف‌دار دریافت شد'
      }
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // ========== متدهای سبد خرید ==========

  // دریافت سبد خرید
  async getCart() {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${PRODUCT_ENDPOINTS.GET_CART}`
      )

      return {
        success: true,
        data: response.data,
        message: 'سبد خرید دریافت شد'
      }
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // افزودن به سبد خرید
  async addToCart(productId, quantity = 1, options = {}) {
    try {
      const response = await api.post(
        `${API_CONFIG.BASE_URL}${PRODUCT_ENDPOINTS.ADD_TO_CART}`,
        {
          product_id: productId,
          quantity,
          ...options
        }
      )

      return {
        success: true,
        data: response.data,
        message: 'به سبد خرید اضافه شد'
      }
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // حذف از سبد خرید
  async removeFromCart(cartItemId) {
    try {
      const response = await api.delete(
        `${API_CONFIG.BASE_URL}${PRODUCT_ENDPOINTS.REMOVE_FROM_CART}/${cartItemId}`
      )

      return {
        success: true,
        data: response.data,
        message: 'از سبد خرید حذف شد'
      }
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // بروزرسانی سبد خرید
  async updateCartItem(cartItemId, quantity) {
    try {
      const response = await api.put(
        `${API_CONFIG.BASE_URL}${PRODUCT_ENDPOINTS.UPDATE_CART}/${cartItemId}`,
        { quantity }
      )

      return {
        success: true,
        data: response.data,
        message: 'سبد خرید بروزرسانی شد'
      }
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // ========== متدهای علاقه‌مندی‌ها ==========

  // دریافت لیست علاقه‌مندی‌ها
  async getWishlist() {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${PRODUCT_ENDPOINTS.WISHLIST}`
      )

      return {
        success: true,
        data: response.data,
        message: 'لیست علاقه‌مندی‌ها دریافت شد'
      }
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // افزودن به علاقه‌مندی‌ها
  async addToWishlist(productId) {
    try {
      const response = await api.post(
        `${API_CONFIG.BASE_URL}${PRODUCT_ENDPOINTS.ADD_TO_WISHLIST}`,
        { product_id: productId }
      )

      return {
        success: true,
        data: response.data,
        message: 'به علاقه‌مندی‌ها اضافه شد'
      }
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // حذف از علاقه‌مندی‌ها
  async removeFromWishlist(productId) {
    try {
      const response = await api.delete(
        `${API_CONFIG.BASE_URL}${PRODUCT_ENDPOINTS.REMOVE_FROM_WISHLIST}/${productId}`
      )

      return {
        success: true,
        data: response.data,
        message: 'از علاقه‌مندی‌ها حذف شد'
      }
    } catch (error) {
      throw this.handleError(error)
    }
  }

  // بررسی آیا محصول در علاقه‌مندی‌ها است
  async isInWishlist(productId) {
    try {
      const wishlist = await this.getWishlist()
      return wishlist.data?.items?.some(item => item.product_id === productId) || false
    } catch (error) {
      return false
    }
  }

  // ========== متدهای کمکی ==========

  // فرمت کردن قیمت برای نمایش
  formatPrice(price) {
    if (!price && price !== 0) return '۰'
    return new Intl.NumberFormat('fa-IR').format(price)
  }

  // محاسبه قیمت با تخفیف
  calculateDiscountedPrice(price, discount) {
    if (!discount || discount <= 0) return price

    const discountAmount = (price * discount) / 100
    return Math.max(0, price - discountAmount)
  }

  // تولید URL تصویر محصول
  getProductImageUrl(imagePath) {
    if (!imagePath) return null

    if (imagePath.startsWith('http')) {
      return imagePath
    }

    return `${API_CONFIG.BASE_URL}/storage/${imagePath}`
  }

  // هندل کردن خطاها
  handleError(error) {

    let errorMessage = 'خطای ناشناخته رخ داده است'
    let errorCode = 'UNKNOWN_ERROR'
    let errorData = null

    if (error.response) {
      errorData = error.response.data
      errorMessage = errorData?.message || 'خطای سرور رخ داده است'
      errorCode = errorData?.code || `HTTP_${error.response.status}`

      switch (error.response.status) {
        case 401:
          errorMessage = 'لطفا ابتدا وارد شوید'
          break
        case 403:
          errorMessage = 'دسترسی غیرمجاز'
          break
        case 404:
          errorMessage = 'محصول مورد نظر یافت نشد'
          break
        case 422:
          errorMessage = 'اطلاعات وارد شده نامعتبر است'
          break
        case 429:
          errorMessage = 'درخواست‌های زیادی ارسال کرده‌اید. لطفا کمی صبر کنید'
          break
        case 500:
          errorMessage = 'خطای سرور. لطفا دوباره تلاش کنید'
          break
      }
    } else if (error.request) {
      errorMessage = 'اتصال به سرور برقرار نشد. لطفا اتصال اینترنت خود را بررسی کنید'
      errorCode = 'NETWORK_ERROR'
    } else {
      errorMessage = error.message || 'خطای ناشناخته'
    }

    return {
      success: false,
      message: errorMessage,
      code: errorCode,
      data: errorData,
      originalError: error
    }
  }

  // ایجاد ساختار استاندارد محصول
  normalizeProduct(product) {
    if (!product) return null

    return {
      id: product.id || product.product_id,
      name: product.name || product.title || 'نام نامشخص',
      slug: product.slug || '',
      description: product.description || '',
      short_description: product.short_description || '',
      price: parseFloat(product.price) || 0,
      originalPrice: parseFloat(product.original_price || product.base_price || product.price) || 0,
      discount: parseInt(product.discount || product.discount_percent || 0),
      discountPrice: this.calculateDiscountedPrice(
        parseFloat(product.original_price || product.base_price || product.price) || 0,
        parseInt(product.discount || product.discount_percent || 0)
      ),
      stock: parseInt(product.stock || product.quantity || 0),
      images: this.normalizeProductImages(product.images || product.image || []),
      category: this.normalizeCategory(product.category),
      brand: this.normalizeBrand(product.brand),
      rating: parseFloat(product.rating || product.average_rating || 0),
      reviewCount: parseInt(product.review_count || 0),
      isAvailable: product.is_available !== false && (parseInt(product.stock || 0) > 0),
      createdAt: product.created_at,
      updatedAt: product.updated_at,
      specifications: product.specifications || product.attributes || {},
      variants: product.variants || [],
      tags: product.tags || []
    }
  }

  // نرمالایز کردن تصاویر محصول
  normalizeProductImages(images) {
    if (!images) return []

    if (typeof images === 'string') {
      return [{ url: this.getProductImageUrl(images), isMain: true }]
    }

    if (Array.isArray(images)) {
      return images.map((img, index) => {
        if (typeof img === 'string') {
          return {
            url: this.getProductImageUrl(img),
            isMain: index === 0
          }
        }

        return {
          url: this.getProductImageUrl(img.url || img.path),
          isMain: img.is_main || index === 0,
          alt: img.alt || ''
        }
      })
    }

    return []
  }

  // نرمالایز کردن دسته‌بندی
  normalizeCategory(category) {
    if (!category) return null

    return {
      id: category.id,
      name: category.name || category.title,
      slug: category.slug,
      parentId: category.parent_id,
      image: category.image ? this.getProductImageUrl(category.image) : null
    }
  }

  // نرمالایز کردن برند
  normalizeBrand(brand) {
    if (!brand) return null

    return {
      id: brand.id,
      name: brand.name,
      slug: brand.slug,
      logo: brand.logo ? this.getProductImageUrl(brand.logo) : null
    }
  }

  // نرمالایز کردن لیست محصولات
  normalizeProducts(products) {
    if (!Array.isArray(products)) return []

    return products.map(product => this.normalizeProduct(product))
  }

  // فیلتر کردن محصولات بر اساس پارامترها
  filterProducts(products, filters = {}) {
    if (!Array.isArray(products)) return []

    let filtered = [...products]

    // فیلتر بر اساس دسته‌بندی
    if (filters.categoryId) {
      filtered = filtered.filter(product =>
        product.category?.id === filters.categoryId
      )
    }

    // فیلتر بر اساس برند
    if (filters.brandId) {
      filtered = filtered.filter(product =>
        product.brand?.id === filters.brandId
      )
    }

    // فیلتر بر اساس قیمت
    if (filters.minPrice !== undefined) {
      filtered = filtered.filter(product =>
        product.discountPrice >= filters.minPrice
      )
    }

    if (filters.maxPrice !== undefined) {
      filtered = filtered.filter(product =>
        product.discountPrice <= filters.maxPrice
      )
    }

    // فیلتر بر اساس موجودی
    if (filters.inStock) {
      filtered = filtered.filter(product => product.isAvailable)
    }

    // فیلتر بر اساس تخفیف
    if (filters.hasDiscount) {
      filtered = filtered.filter(product => product.discount > 0)
    }

    // مرتب‌سازی
    if (filters.sortBy) {
      switch (filters.sortBy) {
        case 'price_asc':
          filtered.sort((a, b) => a.discountPrice - b.discountPrice)
          break
        case 'price_desc':
          filtered.sort((a, b) => b.discountPrice - a.discountPrice)
          break
        case 'newest':
          filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          break
        case 'popular':
          filtered.sort((a, b) => b.rating - a.rating)
          break
        case 'discount':
          filtered.sort((a, b) => b.discount - a.discount)
          break
      }
    }

    return filtered
  }
}

// ایجاد یک instance از ProductService
const productServiceInstance = new ProductService()

export default productServiceInstance